import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Divider,
  Stack,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { NavLink } from "react-router";
import { TableHeaderRow, TableShell, TableCell } from "../ui/TableShell";

const STATEMENT_TABS = [
  { key: "metrics", label: "Metrics" },
  { key: "income", label: "Income" },
  { key: "balance_sheet", label: "Balance Sheet" },
  { key: "cash_flow", label: "Cash Flow" },
];
const UNIT_OPTIONS = [
  { value: "raw", label: "Raw", factor: 1 },
  { value: "thousands", label: "Thousands", factor: 1e3 },
  { value: "millions", label: "Millions", factor: 1e6 },
  { value: "billions", label: "Billions", factor: 1e9 },
];
const STATEMENT_KEY_MAP = {
  income: "income",
  balance: "balance_sheet",
  cash: "cash_flow",
};

const CALCULATED_METRIC_DEFINITIONS = {
  market_cap: { statement: "balance", label: "Market cap" },
  gross_margin: { statement: "income", label: "Gross margin" },
  operating_margin: { statement: "income", label: "Operating margin" },
  ebitda_margin: { statement: "income", label: "EBITDA margin" },
  net_margin: { statement: "income", label: "Net margin" },
  operating_cash_flow_margin: { statement: "cash", label: "Operating cash flow margin" },
  free_cash_flow_margin: { statement: "cash", label: "Free cash flow margin" },
  free_cash_flow_to_ebitda: { statement: "cash", label: "Free cash flow to EBITDA" },
  current_ratio: { statement: "balance", label: "Current ratio" },
  cash_ratio: { statement: "balance", label: "Cash ratio" },
  debt_to_equity: { statement: "balance", label: "Debt to equity" },
  net_debt_to_ebitda: { statement: "balance", label: "Net debt to EBITDA" },
  interest_coverage: { statement: "income", label: "Interest coverage" },
  return_on_assets: { statement: "income", label: "Return on assets (ROA)" },
  return_on_equity: { statement: "income", label: "Return on equity (ROE)" },
  capex_to_revenue: { statement: "cash", label: "Capex to revenue" },
  cash_runway: { statement: "cash", label: "Cash runway (months)" },
  sga_intensity: { statement: "income", label: "SG&A intensity" },
  equity_financing_dependence: { statement: "cash", label: "Equity financing dependence" },
  capex_to_total_assets: { statement: "cash", label: "Capex to total assets" },
  net_debt_to_total_assets: { statement: "balance", label: "Net debt to total assets" },
  reinvestment_ratio: { statement: "cash", label: "Reinvestment ratio" },
};

const LIFECYCLE_OVERVIEW_BLOCKS = {
  exploration: [
    {
      key: "liquidity_runway",
      label: "Liquidity & Runway",
      items: ["cash_runway", "cash_ratio", "current_ratio"],
    },
    {
      key: "burn_dilution",
      label: "Burn & Dilution",
      items: ["free_cash_flow_to_ebitda", "equity_financing_dependence", "sga_intensity"],
    },
    {
      key: "capital_structure",
      label: "Capital Structure",
      items: ["debt_to_equity", "net_debt_to_total_assets"],
    },
  ],
  development: [
    {
      key: "liquidity_runway",
      label: "Liquidity & Runway",
      items: ["cash_runway", "cash_ratio", "current_ratio"],
    },
    {
      key: "capital_intensity",
      label: "Capital Intensity",
      items: ["capex_to_total_assets", "reinvestment_ratio", "capex_to_revenue"],
    },
    {
      key: "leverage_funding_risk",
      label: "Leverage & Funding Risk",
      items: ["net_debt_to_total_assets", "debt_to_equity", "net_debt_to_ebitda"],
    },
  ],
  production: [
    {
      key: "profitability",
      label: "Profitability",
      items: ["gross_margin", "ebitda_margin", "net_margin"],
    },
    {
      key: "cash_generation",
      label: "Cash Generation & Reinvestment",
      items: ["operating_cash_flow_margin", "free_cash_flow_margin", "reinvestment_ratio"],
    },
    {
      key: "leverage_liquidity",
      label: "Leverage & Liquidity",
      items: ["net_debt_to_ebitda", "interest_coverage", "current_ratio"],
    },
  ],
};
const METRICS_DETAIL_BLOCKS = [
  {
    key: "valuation",
    label: "Valuation",
    items: [{ code: "market_cap", label: "Market cap" }],
  },
  {
    key: "profitability",
    label: "Profitability",
    items: [
      { code: "gross_margin" },
      { code: "operating_margin" },
      { code: "ebitda_margin" },
      { code: "net_margin" },
      { code: "return_on_assets", label: "Return on assets (ROA)" },
      { code: "return_on_equity", label: "Return on equity (ROE)" },
    ],
  },
  {
    key: "liquidity",
    label: "Liquidity",
    items: [{ code: "current_ratio" }, { code: "cash_ratio" }],
  },
  {
    key: "leverage",
    label: "Leverage",
    items: [
      { code: "debt_to_equity" },
      { code: "net_debt_to_ebitda" },
      { code: "net_debt_to_total_assets" },
    ],
  },
  {
    key: "cash_capital",
    label: "Cash & Capital",
    items: [
      { code: "operating_cash_flow_margin" },
      { code: "free_cash_flow_margin" },
      { code: "free_cash_flow_to_ebitda" },
      { code: "reinvestment_ratio" },
      { code: "capex_to_revenue" },
      { code: "capex_to_total_assets" },
    ],
  },
  {
    key: "mining_specific",
    label: "Mining-Specific",
    items: [
      { code: "cash_runway" },
      { code: "sga_intensity" },
      { code: "equity_financing_dependence" },
    ],
  },
];
const METRIC_DEFINITIONS = {
  market_cap: {
    label: "Market cap",
    definition: "Ending period share price multiplied by diluted shares outstanding.",
    interpretation:
      "Represents the equity market value at period end. Sensitive to both share price moves and dilution.",
  },
  gross_margin: {
    label: "Gross Margin",
    definition: "Gross profit divided by revenue.",
    interpretation:
      "Measures how much revenue remains after direct production costs. Higher margins indicate stronger unit economics and cost discipline.",
  },
  operating_margin: {
    label: "Operating Margin",
    definition: "Operating income divided by revenue.",
    interpretation:
      "Shows profitability after operating expenses but before interest and taxes. Reflects operational efficiency.",
  },
  ebitda_margin: {
    label: "EBITDA Margin",
    definition: "EBITDA divided by revenue.",
    interpretation:
      "Measures core operating profitability before capital structure and accounting effects. Commonly used in mining valuation and debt analysis.",
  },
  net_margin: {
    label: "Net Margin",
    definition: "Net income divided by revenue.",
    interpretation: "Represents bottom-line profitability after all expenses.",
  },
  return_on_assets: {
    label: "Return on Assets (ROA)",
    definition: "Net income divided by total assets.",
    interpretation:
      "Indicates how efficiently assets generate profit. Mining is capital intensive, so ROA is typically lower than other sectors.",
  },
  return_on_equity: {
    label: "Return on Equity (ROE)",
    definition: "Net income divided by shareholders’ equity.",
    interpretation:
      "Measures return generated for equity holders. High volatility is common in mining cycles.",
  },
  current_ratio: {
    label: "Current Ratio",
    definition: "Current assets divided by current liabilities.",
    interpretation:
      "Indicates ability to meet short-term obligations. Below 1.0 may signal liquidity risk.",
  },
  cash_ratio: {
    label: "Cash Ratio",
    definition: "Cash and equivalents divided by current liabilities.",
    interpretation:
      "Conservative liquidity measure focusing only on immediately available funds.",
  },
  debt_to_equity: {
    label: "Debt to Equity",
    definition: "Total debt divided by shareholders’ equity.",
    interpretation:
      "Measures financial leverage. Higher values indicate greater reliance on debt financing.",
  },
  net_debt_to_ebitda: {
    label: "Net Debt to EBITDA",
    definition: "(Net debt) divided by EBITDA.",
    interpretation:
      "Indicates how many years of EBITDA would be required to repay net debt. Common covenant metric for producers.",
  },
  net_debt_to_total_assets: {
    label: "Net Debt to Total Assets",
    definition: "(Net debt) divided by total assets.",
    interpretation:
      "Shows how much of the asset base is funded by net borrowing.",
  },
  operating_cash_flow_margin: {
    label: "Operating Cash Flow Margin",
    definition: "Operating cash flow divided by revenue.",
    interpretation: "Indicates how effectively revenue converts to operating cash.",
  },
  free_cash_flow_margin: {
    label: "Free Cash Flow Margin",
    definition: "Free cash flow divided by revenue.",
    interpretation:
      "Measures the percentage of revenue converted into free cash after capital expenditures.",
  },
  free_cash_flow_to_ebitda: {
    label: "Free Cash Flow to EBITDA",
    definition: "Free cash flow divided by EBITDA.",
    interpretation:
      "Shows capital intensity and cash conversion quality.",
  },
  reinvestment_ratio: {
    label: "Reinvestment Ratio",
    definition: "Capital expenditures divided by operating cash flow.",
    interpretation:
      "Indicates how much operating cash is reinvested into the business. Values above 1.0 suggest expansion or strain.",
  },
  capex_to_revenue: {
    label: "Capex to Revenue",
    definition: "Capital expenditures divided by revenue.",
    interpretation: "Measures investment intensity relative to sales.",
  },
  capex_to_total_assets: {
    label: "Capex to Total Assets",
    definition: "Capital expenditures divided by total assets.",
    interpretation:
      "Indicates the scale of capital investment relative to the asset base. Higher values are typical during development phases.",
  },
  cash_runway: {
    label: "Cash Runway (Months)",
    definition: "Cash and equivalents divided by operating cash flow burn, expressed in months.",
    interpretation:
      "Estimates how long a company can operate at its current burn rate without additional financing. Critical for exploration-stage companies.",
  },
  sga_intensity: {
    label: "SG&A Intensity",
    definition: "SG&A expense divided by total operating expense.",
    interpretation:
      "Measures how much of operating cost is corporate overhead rather than operational activity. Elevated levels may indicate overhead-heavy structure.",
  },
  equity_financing_dependence: {
    label: "Equity Financing Dependence",
    definition: "Common stock issuance divided by free cash flow burn.",
    interpretation:
      "Indicates the extent to which operating deficits are funded through equity issuance. High values suggest dilution-driven financing.",
  },
};

const formatDateLabel = (value) => {
  if (!value) return "";

  // If it's YYYY-MM-DD (date-only), parse as local date
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const localDate = new Date(year, month - 1, day); // local midnight
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(localDate);
  }

  // Fallback for full ISO timestamps
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(parsed);
};

const MARGIN_RATIO_CODES = new Set([
  "gross_margin",
  "operating_margin",
  "ebitda_margin",
  "net_margin",
  "operating_cash_flow_margin",
  "free_cash_flow_margin",
  "capex_to_revenue",
  "sga_intensity",
  "equity_financing_dependence",
  "capex_to_total_assets",
  "net_debt_to_total_assets",
  "reinvestment_ratio",
  "return_on_assets",
  "return_on_equity",
]);
const MULTIPLE_RATIO_CODES = new Set([
  "current_ratio",
  "cash_ratio",
  "debt_to_equity",
  "net_debt_to_ebitda",
  "interest_coverage",
  "free_cash_flow_to_ebitda",
]);

const getRatioDisplayStyle = (code) => {
  if (MARGIN_RATIO_CODES.has(code)) return "percent";
  if (MULTIPLE_RATIO_CODES.has(code)) return "multiple";
  return "plain";
};

const ROW_PY = 0.85;
const SUBTOTAL_CODES = new Set([
  "gross_profit",
  "operating_income",
  "pretax_income",
  "net_income",
  "assets_total_assets",
  "liabilities_total_liabilities",
  "end_cash_position",
]);
const METRIC_THRESHOLDS = {
  gross_margin: { direction: "higher_better", good: 0.35, neutral: 0.15 },
  ebitda_margin: { direction: "higher_better", good: 0.3, neutral: 0.1 },
  net_margin: { direction: "higher_better", good: 0.15, neutral: 0.0 },
  operating_cash_flow_margin: { direction: "higher_better", good: 0.25, neutral: 0.05 },
  free_cash_flow_margin: { direction: "higher_better", good: 0.15, neutral: 0.0 },
  free_cash_flow_to_ebitda: { direction: "higher_better", good: 0.5, neutral: 0.2 },
  current_ratio: { direction: "higher_better", good: 1.5, neutral: 1.0 },
  cash_ratio: { direction: "higher_better", good: 0.75, neutral: 0.4 },
  debt_to_equity: { direction: "lower_better", good: 0.5, neutral: 1.0 },
  net_debt_to_ebitda: { direction: "lower_better", good: 1.5, neutral: 3.0 },
  interest_coverage: { direction: "higher_better", good: 4.0, neutral: 2.0 },
  return_on_assets: { direction: "higher_better", good: 0.08, neutral: 0.02 },
  return_on_equity: { direction: "higher_better", good: 0.15, neutral: 0.05 },
  capex_to_revenue: { direction: "lower_better", good: 0.2, neutral: 0.4 },

  cash_runway: { direction: "higher_better", good: 24.0, neutral: 12.0 },
  sga_intensity: { direction: "lower_better", good: 0.25, neutral: 0.4 },
  equity_financing_dependence: { direction: "lower_better", good: 0.3, neutral: 0.7 },
  capex_to_total_assets: { direction: "higher_better", good: 0.25, neutral: 0.1 },
  net_debt_to_total_assets: { direction: "lower_better", good: 0.25, neutral: 0.5 },
  reinvestment_ratio: { direction: "balanced", good: 0.6, neutral: 1.2 },
};

const getMetricValueColor = (value, code) => {
  if (value === null || value === undefined) return "text.secondary";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "text.secondary";
  const threshold = METRIC_THRESHOLDS[code];
  if (!threshold) return "text.primary";
  if (threshold.direction === "higher_better") {
    if (numeric >= threshold.good) return  "success.main";
    if (numeric >= threshold.neutral) return "text.secondary";
    return "error.main";
  }
  if (threshold.direction === "balanced") {
    if (numeric >= threshold.good && numeric <= threshold.neutral) return "success.main";
    if (numeric >= threshold.good * 0.75 && numeric <= threshold.neutral * 1.25) {
      return "text.secondary";
    }
    return "error.main";
  }
  if (numeric <= threshold.good) return  "success.main";
  if (numeric <= threshold.neutral) return "text.secondary";
  return "error.main";
};

const formatFinancialValue = (value, unitType, scale, code) => {
  if (value === null || value === undefined) return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return "—";
  if (code === "cash_runway") {
    return `${new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(parsed)} mo`;
  }
  if (unitType === "ratio") {
    const style = getRatioDisplayStyle(code);
    if (style === "percent") {
      return `${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(parsed * 100)}%`;
    }
    if (style === "multiple") {
      return `${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parsed)}x`;
    }
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(parsed);
  }
  const scaled = parsed / scale;
  const abs = Math.abs(scaled);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(scaled);
};


const getPercentChangeDisplay = (value) => {
  if (value === null || value === undefined) {
    return { text: "N/A", color: "text.secondary" };
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return { text: "N/A", color: "text.secondary" };
  }
  if (parsed === 0) {
    return { text: "0.0%", color: "text.secondary" };
  }
  const sign = parsed > 0 ? "+" : "";
  return {
    text: `${sign}${parsed.toFixed(1)}%`,
    color: parsed > 0 ? "success.main" : "error.main",
  };
};

const hasAnyValue = (series) =>
  Array.isArray(series) && series.some((value) => value !== null && value !== undefined);

const formatCompactValue = (value, unitType = "currency", code) => {
  if (value === null || value === undefined) return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return "—";
  if (unitType === "ratio") {
    const style = getRatioDisplayStyle(code);
    if (style === "percent") {
      return `${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(parsed * 100)}%`;
    }
    if (style === "multiple") {
      return `${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parsed)}x`;
    }
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(parsed);
  }
  return new Intl.NumberFormat(undefined, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 2,
  }).format(parsed);
};

const getPercentChange = (latest, previous) => {
  if (latest === null || latest === undefined) return null;
  if (previous === null || previous === undefined) return null;
  const prev = Number(previous);
  const curr = Number(latest);
  if (Number.isNaN(prev) || Number.isNaN(curr) || prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

const combineMetricPairs = (...pairs) => {
  const sumValues = (key) => {
    const values = pairs
      .map((pair) => pair?.[key])
      .filter((value) => value !== null && value !== undefined);
    if (!values.length) return null;
    return values.reduce((sum, value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);
  };
  return {
    latest: sumValues("latest"),
    previous: sumValues("previous"),
  };
};

const buildStatementLayout = (rows, statementKey) => {
  const rowByCode = rows.reduce((acc, row) => {
    acc[row.code] = row;
    return acc;
  }, {});
  const layout = [];

  const addSection = (label) =>
    layout.push({
      type: "section_header",
      label,
    });
  const addRow = (code, label) => {
    const row = rowByCode[code];
    if (!row) return;
    layout.push({ type: "row", row: { ...row, label: label || row.label } });
  };
  const addGroup = (key, label, codes, totalCode, childLabelMap) => {
    const rowsForGroup = (codes || [])
      .map((item) => {
        if (typeof item === "string") {
          const row = rowByCode[item];
          if (!row) return null;
          return { ...row, label: childLabelMap?.[item] || row.label };
        }
        if (item && item.type === "subgroup") {
          const subgroupRows = (item.codes || [])
            .map((code) => {
              const row = rowByCode[code];
              if (!row) return null;
              return { ...row, label: item.childLabelMap?.[code] || row.label };
            })
            .filter(Boolean);
          const subgroupTotalRow = item.totalCode ? rowByCode[item.totalCode] : null;
          if (!subgroupRows.length && !subgroupTotalRow) return null;
          return {
            type: "subgroup",
            key: item.key,
            label: item.label,
            rows: subgroupRows,
            totalRow: subgroupTotalRow,
          };
        }
        return null;
      })
      .filter(Boolean);
    const totalRow = totalCode ? rowByCode[totalCode] : null;
    if (!rowsForGroup.length && !totalRow) return;
    layout.push({
      type: "group",
      key,
      label,
      rows: rowsForGroup,
      totalRow,
    });
  };

  if (statementKey === "income") {
    addRow("sales", "Sales");
    addRow("cost_of_goods", "Cost of goods");
    addRow("gross_profit", "Gross profit");
    addGroup(
      "operating_expense",
      "Operating expenses",
      [
        "operating_expense_research_and_development",
        "operating_expense_selling_general_and_administrative",
        "operating_expense_other_operating_expenses",
      ],
      "operating_expense_total",
      {
        operating_expense_research_and_development: "R&D",
        operating_expense_selling_general_and_administrative: "SG&A",
        operating_expense_other_operating_expenses: "Other operating expenses",
      }
    );
    addRow("operating_income", "Operating income");
    addGroup(
      "non_operating",
      "Non-operating",
      [
        "non_operating_interest_income",
        "non_operating_interest_expense",
      ],
      "non_operating_expense_total",
      {
        non_operating_interest_income: "Interest income",
        non_operating_interest_expense: "Interest expense",
      }
    );
    addRow("other_income_expense", "Other income/expense")
    addRow("pretax_income", "Pretax income");
    addRow("income_tax", "Income tax");
    addRow("net_income", "Net income");
    addRow("ebit", "EBIT");
    addRow("ebitda", "EBITDA");
    addSection("Share Data");
    addRow("basic_shares_outstanding", "Shares outstanding (basic)");
    addRow("diluted_shares_outstanding", "Shares outstanding (diluted)");
    addRow("eps_basic", "EPS (basic)");
    addRow("eps_diluted", "EPS (diluted)");
  }

  if (statementKey === "balance_sheet") {
    addSection("Assets");
    const currentAssetCodes = rows
      .filter(
        (row) =>
          row.code.startsWith("assets_current_assets_") &&
          row.code !== "assets_current_assets_total_current_assets"
      )
      .map((row) => row.code);
    const normalizedCurrentAssetCodes = currentAssetCodes.filter(
      (code) =>
        code !== "assets_current_assets_cash" &&
        code !== "assets_current_assets_cash_equivalents" &&
        code !== "assets_current_assets_cash_and_cash_equivalents"
    );
    addGroup(
      "current_assets",
      "Current assets",
      [
        {
          type: "subgroup",
          key: "cash_and_equivalents",
          label: "Cash & cash equivalents",
          codes: [
            "assets_current_assets_cash",
            "assets_current_assets_cash_equivalents",
          ],
          totalCode: "assets_current_assets_cash_and_cash_equivalents",
        },
        ...normalizedCurrentAssetCodes,
      ],
      "assets_current_assets_total_current_assets"
    );
    addGroup(
      "non_current_assets",
      "Non current assets",
      rows
        .filter((row) => row.code.startsWith("assets_non_current_assets_") && row.code !== "assets_non_current_assets_total_non_current_assets")
        .map((row) => row.code),
      "assets_non_current_assets_total_non_current_assets"
    );
    addRow("assets_total_assets", "Total assets");

    addSection("Liabilities");
    addGroup(
      "current_liabilities",
      "Current liabilities",
      rows
        .filter((row) => row.code.startsWith("liabilities_current_liabilities_") && row.code !== "liabilities_current_liabilities_total_current_liabilities")
        .map((row) => row.code),
      "liabilities_current_liabilities_total_current_liabilities"
    );
    addGroup(
      "non_current_liabilities",
      "Non current liabilities",
      rows
        .filter((row) => row.code.startsWith("liabilities_non_current_liabilities_") && row.code !== "liabilities_non_current_liabilities_total_non_current_liabilities")
        .map((row) => row.code),
      "liabilities_non_current_liabilities_total_non_current_liabilities"
    );
    addRow("liabilities_total_liabilities", "Total liabilities");

    addSection("Equity");
    addGroup(
      "shareholders_equity",
      "Total shareholders' equity",
      [
        "shareholders_equity_common_stock",
        "shareholders_equity_additional_paid_in_capital",
        "shareholders_equity_retained_earnings",
        "shareholders_equity_other_shareholders_equity",
        "shareholders_equity_treasury_stock",
        "shareholders_equity_minority_interest",
      ],
      "shareholders_equity_total_shareholders_equity",
      {
        shareholders_equity_common_stock: "Common stock",
        shareholders_equity_additional_paid_in_capital: "Additional paid-in capital",
        shareholders_equity_retained_earnings: "Retained earnings",
        shareholders_equity_other_shareholders_equity: "Other shareholders' equity",
        shareholders_equity_treasury_stock: "Treasury stock",
        shareholders_equity_minority_interest: "Minority interest",
      }
    );
  }

  if (statementKey === "cash_flow") {
    addSection("Operating Activities");
    addGroup(
      "operating_activities",
      "Operating Activities",
      rows
        .filter((row) => row.code.startsWith("operating_activities_") && row.code !== "operating_activities_operating_cash_flow")
        .map((row) => row.code),
      "operating_activities_operating_cash_flow"
    );

    addSection("Investing Activities");
    addGroup(
      "investing_activities",
      "Investing Activities",
      rows
        .filter((row) => row.code.startsWith("investing_activities_") && row.code !== "investing_activities_investing_cash_flow")
        .map((row) => row.code),
      "investing_activities_investing_cash_flow"
    );

    addSection("Financing Activities");
    addGroup(
      "financing_activities",
      "Financing Activities",
      rows
        .filter((row) => row.code.startsWith("financing_activities_") && row.code !== "financing_activities_financing_cash_flow")
        .map((row) => row.code),
      "financing_activities_financing_cash_flow"
    );

    addRow("end_cash_position", "End cash position");
    addSection("Cash Metrics")
    addRow("income_tax_paid", "Income tax paid");
    addRow("interest_paid", "Interest paid");
    addRow("free_cash_flow", "Free cash flow");
  }

  return layout;
};

const isRightAlignedValue = (unitType) => unitType === "ratio" || unitType === "currency" || unitType === "shares";

export default function FinancialsTab({ company }) {
  const financials = company?.financials || null;
  const loading = false;
  const error = null;
  const [infoOpen, setInfoOpen] = useState(false);
  const [statementIndex, setStatementIndex] = useState(0);
  const [granularity, setGranularity] = useState("ttm");
  const [unitScale, setUnitScale] = useState("millions");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [currencyMode, setCurrencyMode] = useState("reported");

  const tabKey = STATEMENT_TABS[statementIndex]?.key;
  const isOverview = tabKey === "metrics";
  const statementKey = isOverview ? "income" : tabKey;
  const statement = financials?.statements?.[statementKey] || null;
  const rows = useMemo(() => statement?.rows || [], [statement]);
  const periods = statement?.[granularity]?.periods || [];
  const currency = financials?.currency || "";
  const hasUsd = Boolean(financials?.has_usd);
  const reportedCurrency = (currency || "").toUpperCase();
  const showCurrencyToggle = hasUsd && reportedCurrency && reportedCurrency !== "USD";
  const activeCurrencyMode = showCurrencyToggle ? currencyMode : "usd";
  const scale = UNIT_OPTIONS.find((option) => option.value === unitScale)?.factor ?? 1;
  const annualPeriods = financials?.statements?.income?.annual?.periods || [];
  const quarterlyPeriods = financials?.statements?.income?.quarterly?.periods || [];
  const ttmPeriods = financials?.statements?.income?.ttm?.periods || [];
  const hasTtm = ttmPeriods.length > 0;
  const hasAnnual = annualPeriods.length > 0;
  const hasQuarterly = quarterlyPeriods.length > 0;
  const defaultGranularity = hasTtm ? "ttm" : hasAnnual ? "annual" : hasQuarterly ? "quarterly" : "ttm";

  useEffect(() => {
    const isCurrentAvailable =
      (granularity === "ttm" && hasTtm) ||
      (granularity === "annual" && hasAnnual) ||
      (granularity === "quarterly" && hasQuarterly);
    if (!isCurrentAvailable && granularity !== defaultGranularity) {
      setGranularity(defaultGranularity);
    }
  }, [granularity, hasTtm, hasAnnual, hasQuarterly, defaultGranularity]);

  const bannerGranularity =
    (granularity === "annual" && hasAnnual) ||
    (granularity === "quarterly" && hasQuarterly) ||
    (granularity === "ttm" && hasTtm)
      ? granularity
      : hasTtm
      ? "ttm"
      : hasAnnual
      ? "annual"
      : hasQuarterly
      ? "quarterly"
      : null;
  const bannerPeriods =
    bannerGranularity === "annual"
      ? annualPeriods
      : bannerGranularity === "quarterly"
      ? quarterlyPeriods
      : ttmPeriods;
  const bannerHasData = Boolean(bannerGranularity && bannerPeriods.length);
  const hasFinancials =
    bannerHasData &&
    ((financials?.statements?.income?.rows || []).length > 0 ||
      (financials?.statements?.balance_sheet?.rows || []).length > 0 ||
      (financials?.statements?.cash_flow?.rows || []).length > 0);
  const layout = useMemo(
    () => (rows.length ? buildStatementLayout(rows, statementKey) : []),
    [rows, statementKey]
  );
  const overviewPeriods = financials?.statements?.income?.[granularity]?.periods || [];
  const displayPeriods = isOverview ? overviewPeriods : periods;
  const columnTemplate = useMemo(
    () => `2.4fr repeat(${Math.max(displayPeriods.length, 1)}, 1fr)`,
    [displayPeriods.length]
  );
  const allGroupKeys = useMemo(() => {
    const keys = [];
    layout.forEach((entry) => {
      if (entry.type !== "group") return;
      keys.push(entry.key);
      (entry.rows || []).forEach((row) => {
        if (row?.type === "subgroup") {
          keys.push(`${entry.key}::${row.key}`);
        }
      });
    });
    return keys;
  }, [layout]);
  const hasExpandableGroups = useMemo(() => allGroupKeys.length > 0, [allGroupKeys]);
  const showExpandCollapse = !isOverview && hasExpandableGroups;
  const unitTypeByCode = useMemo(() => {
    const map = {};
    Object.values(financials?.statements || {}).forEach((statementBlock) => {
      (statementBlock?.rows || []).forEach((row) => {
        map[row.code] = row.unit_type;
      });
    });
    return map;
  }, [financials]);
  const rowMetaByStatement = useMemo(() => {
    const map = {};
    Object.entries(financials?.statements || {}).forEach(([key, statementBlock]) => {
      const rowMap = {};
      (statementBlock?.rows || []).forEach((row) => {
        rowMap[row.code] = row;
      });
      map[key] = rowMap;
    });
    return map;
  }, [financials]);

  const profile =
    company?.profile || company?.published_profile || company?.draft_profile || {};
  const lifecycleValue = (profile?.company_lifecycle || "").toString().toLowerCase();
  const lifecycleKey = ["exploration", "development", "production"].includes(lifecycleValue)
    ? lifecycleValue
    : "production";
  const lifecycleLabel = lifecycleKey.charAt(0).toUpperCase() + lifecycleKey.slice(1);
  const overviewBlockDefinitions = useMemo(
    () => LIFECYCLE_OVERVIEW_BLOCKS[lifecycleKey] || LIFECYCLE_OVERVIEW_BLOCKS.production,
    [lifecycleKey]
  );

  const isGroupExpanded = (key) => {
    const current = expandedGroups[statementKey]?.[key];
    return current === true;
  };

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [statementKey]: {
        ...(prev[statementKey] || {}),
        [key]: !isGroupExpanded(key),
      },
    }));
  };
  const expandAllGroups = () => {
    const next = {};
    allGroupKeys.forEach((key) => {
      next[key] = true;
    });
    setExpandedGroups((prev) => ({
      ...prev,
      [statementKey]: next,
    }));
  };
  const collapseAllGroups = () => {
    const next = {};
    allGroupKeys.forEach((key) => {
      next[key] = false;
    });
    setExpandedGroups((prev) => ({
      ...prev,
      [statementKey]: next,
    }));
  };
  const allGroupsExpanded = hasExpandableGroups && allGroupKeys.every((key) => isGroupExpanded(key));

  const getMetricSeries = (statementGroup, code) => {
    if (!bannerGranularity) return [];
    const statementBlock = financials?.statements?.[statementGroup]?.[bannerGranularity] || {};
    const byType = statementBlock.data_by_value_type || {};
    const fallback = statementBlock.data || {};
    const unitType = unitTypeByCode[code];
    if (unitType === "ratio") {
      return (byType.derived?.[code] ?? byType.reported?.[code] ?? fallback[code] ?? []);
    }
    if (unitType === "currency") {
      const preferred = byType?.[activeCurrencyMode] || {};
      const preferredSeries = preferred[code];
      if (hasAnyValue(preferredSeries)) return preferredSeries;
      const reportedSeries = byType.reported?.[code];
      if (hasAnyValue(reportedSeries)) return reportedSeries;
      const derivedSeries = byType.derived?.[code];
      if (hasAnyValue(derivedSeries)) return derivedSeries;
      return fallback[code] ?? [];
    }
    return (byType.reported?.[code] ?? fallback[code] ?? []);
  };

  const getMetricPair = (statementGroup, code) => {
    const series = getMetricSeries(statementGroup, code);
    return {
      latest: series[0] ?? null,
      previous: series[1] ?? null,
    };
  };

  const sales = getMetricPair("income", "sales");
  const netIncome = getMetricPair("income", "net_income");
  const sharesDiluted = getMetricPair("income", "diluted_shares_outstanding");
  const freeCashFlow = getMetricPair("cash_flow", "free_cash_flow");
  const cashAndEquivalentsPrimary = getMetricPair("balance_sheet", "assets_current_assets_cash_and_cash_equivalents");
  const cashAndEquivalentsFallback = getMetricPair("balance_sheet", "assets_current_assets_cash");
  const cashEquivalents =
    cashAndEquivalentsPrimary.latest !== null ? cashAndEquivalentsPrimary : cashAndEquivalentsFallback;
  const shortTermDebt = getMetricPair("balance_sheet", "liabilities_current_liabilities_short_term_debt");
  const longTermDebt = getMetricPair("balance_sheet", "liabilities_non_current_liabilities_long_term_debt");
  const totalDebt = combineMetricPairs(shortTermDebt, longTermDebt);
  const bannerPeriodEnding = bannerPeriods?.[0]?.end_date
    ? formatDateLabel(bannerPeriods[0].end_date)
    : "-";
  const currencyLabel = activeCurrencyMode === "usd" ? "USD" : currency;
  const overviewSnapshotGranularity = bannerGranularity || granularity;
  const getOverviewSnapshotValue = (statementGroup, code) => {
    if (!overviewSnapshotGranularity) return null;
    const statementBlock =
      financials?.statements?.[statementGroup]?.[overviewSnapshotGranularity] || {};
    const byType = statementBlock.data_by_value_type || {};
    const fallback = statementBlock.data || {};
    const unitType = unitTypeByCode[code];
    if (unitType === "ratio") {
      const values = byType.derived?.[code] ?? byType.reported?.[code] ?? fallback[code] ?? [];
      return values[0] ?? null;
    }
    if (unitType === "currency") {
      const preferred = byType?.[activeCurrencyMode] || {};
      const preferredSeries = preferred[code];
      const reportedSeries = byType.reported?.[code];
      const derivedSeries = byType.derived?.[code];
      const values = hasAnyValue(preferredSeries)
        ? preferredSeries
        : hasAnyValue(reportedSeries)
        ? reportedSeries
        : hasAnyValue(derivedSeries)
        ? derivedSeries
        : fallback[code] ?? [];
      return values[0] ?? null;
    }
    const values = byType.reported?.[code] ?? fallback[code] ?? [];
    return values[0] ?? null;
  };
  const rowValuesForDisplay = (row, statementGroup = statementKey) => {
    const statementBlock = financials?.statements?.[statementGroup]?.[granularity] || {};
    const fallback = statementBlock.data || {};
    const byType = statementBlock.data_by_value_type || {};
    if (row.unit_type === "ratio") {
      return byType.derived?.[row.code] ?? byType.reported?.[row.code] ?? fallback[row.code] ?? [];
    }
    if (row.unit_type === "currency") {
      const preferred = byType?.[activeCurrencyMode] || {};
      const preferredSeries = preferred[row.code];
      if (hasAnyValue(preferredSeries)) return preferredSeries;
      const reportedSeries = byType.reported?.[row.code];
      if (hasAnyValue(reportedSeries)) return reportedSeries;
      const derivedSeries = byType.derived?.[row.code];
      if (hasAnyValue(derivedSeries)) return derivedSeries;
      return fallback[row.code] ?? [];
    }
    return byType.reported?.[row.code] ?? fallback[row.code] ?? [];
  };
  const overviewBlocks = useMemo(() => {
    return overviewBlockDefinitions
      .map((block) => {
        const items = block.items
          .map((code) => {
            const definition = CALCULATED_METRIC_DEFINITIONS[code];
            const statementKey = definition?.statement
              ? STATEMENT_KEY_MAP[definition.statement] || definition.statement
              : null;
            const row = statementKey ? rowMetaByStatement?.[statementKey]?.[code] : null;
            if (!row || !statementKey) return null;
            return {
              ...row,
              label: definition?.label || row.label,
              statement: statementKey,
            };
          })
          .filter(Boolean);
        return { ...block, items };
      })
      .filter((block) => block.items.length);
  }, [overviewBlockDefinitions, rowMetaByStatement]);

  const metricsDetailBlocks = useMemo(() => {
    return METRICS_DETAIL_BLOCKS.map((block) => {
      const items = block.items
        .map((item) => {
          const definition = CALCULATED_METRIC_DEFINITIONS[item.code];
          const statementKey = definition?.statement
            ? STATEMENT_KEY_MAP[definition.statement] || definition.statement
            : null;
          const row = statementKey ? rowMetaByStatement?.[statementKey]?.[item.code] : null;
          if (!row || !statementKey) return null;
          return {
            ...row,
            label: item.label || definition?.label || row.label,
            statement: statementKey,
          };
        })
        .filter(Boolean);
      return { ...block, items };
    }).filter((block) => block.items.length);
  }, [rowMetaByStatement]);

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: "100%" }}>
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                Financials
              </Typography>
              {!loading && !error && hasFinancials && (
                <IconButton
                  size="small"
                  onClick={() => setInfoOpen(true)}
                  aria-label="Financials information"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            {hasFinancials && (
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <ToggleButtonGroup
                  value={granularity}
                  exclusive
                  onChange={(_, value) => value && setGranularity(value)}
                  size="small"
                >
                  {hasTtm && (
                    <ToggleButton value="ttm" sx={{ px: 1.5 }}>
                      TTM
                    </ToggleButton>
                  )}
                  <ToggleButton value="annual" sx={{ px: 1.5 }}>
                    Annual
                  </ToggleButton>
                  <ToggleButton value="quarterly" sx={{ px: 1.5 }}>
                    Quarterly
                  </ToggleButton>
                </ToggleButtonGroup>
                {showCurrencyToggle && (
                  <ToggleButtonGroup
                    value={currencyMode}
                    exclusive
                    onChange={(_, value) => value && setCurrencyMode(value)}
                    size="small"
                  >
                    <ToggleButton value="reported" sx={{ px: 1.5 }}>
                      Reported
                    </ToggleButton>
                    <ToggleButton value="usd" sx={{ px: 1.5 }}>
                      USD
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>
            )}
          </Stack>
          {hasFinancials && bannerHasData && (
            <Typography variant="caption" color="text.secondary">
              Period ending: {bannerPeriodEnding || "-"} · Currency: {currencyLabel || "-"} · Source:{" "}
              <NavLink to="https://twelvedata.com/" target="_blank" rel="noopener noreferrer">
                TwelveData
              </NavLink>
            </Typography>
          )}
        </Stack>

        {hasFinancials && (
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1.5,
              backgroundColor: "rgba(34, 35, 37, 0.02)",
              boxShadow: "0 1px 6px rgba(15, 23, 42, 0.06)",
              px: 2.5,
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 2,
                alignItems: "center",
              }}
            >
              <Box sx={{ pr: { md: 2 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.875rem'}}>
                  Sales
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatCompactValue(sales.latest, "currency", "sales")}
                </Typography>
                {(() => {
                  const display = getPercentChangeDisplay(
                    getPercentChange(sales.latest, sales.previous)
                  );
                  return (
                    <Typography variant="caption" color={display.color}>
                      {display.text}
                    </Typography>
                  );
                })()}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.875rem'}}>
                  Net income
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatCompactValue(netIncome.latest, "currency", "net_income")}
                </Typography>
                {(() => {
                  const display = getPercentChangeDisplay(
                    getPercentChange(netIncome.latest, netIncome.previous)
                  );
                  return (
                    <Typography variant="caption" color={display.color}>
                      {display.text}
                    </Typography>
                  );
                })()}
              </Box>
              <Box sx={{ borderLeft: { md: "1px solid rgba(0,0,0,0.08)" }, pl: { md: 2 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.875rem'}}>
                  Cash &amp; equivalents
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatCompactValue(
                    cashEquivalents.latest,
                    "currency",
                    "assets_current_assets_cash_and_cash_equivalents"
                  )}
                </Typography>
                {(() => {
                  const display = getPercentChangeDisplay(
                    getPercentChange(cashEquivalents.latest, cashEquivalents.previous)
                  );
                  return (
                    <Typography variant="caption" color={display.color}>
                      {display.text}
                    </Typography>
                  );
                })()}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.875rem'}}>
                  Shares outstanding (diluted)
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatCompactValue(sharesDiluted.latest, "shares", "diluted_shares_outstanding")}
                </Typography>
                {(() => {
                  const display = getPercentChangeDisplay(
                    getPercentChange(sharesDiluted.latest, sharesDiluted.previous)
                  );
                  return (
                    <Typography variant="caption" color={display.color}>
                      {display.text}
                    </Typography>
                  );
                })()}
              </Box>
              <Box sx={{ borderLeft: { md: "1px solid rgba(0,0,0,0.08)" }, pl: { md: 2 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.875rem'}}>
                  Total debt
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatCompactValue(totalDebt.latest, "currency", "total_debt")}
                </Typography>
                {(() => {
                  const display = getPercentChangeDisplay(
                    getPercentChange(totalDebt.latest, totalDebt.previous)
                  );
                  return (
                    <Typography variant="caption" color={display.color}>
                      {display.text}
                    </Typography>
                  );
                })()}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.875rem'}}>
                  Free cash flow
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatCompactValue(freeCashFlow.latest, "currency", "free_cash_flow")}
                </Typography>
                {(() => {
                  const display = getPercentChangeDisplay(
                    getPercentChange(freeCashFlow.latest, freeCashFlow.previous)
                  );
                  return (
                    <Typography variant="caption" color={display.color}>
                      {display.text}
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          </Box>
        )}

        {hasFinancials && bannerHasData && (
          <Box>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Financial Metrics
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stage: {lifecycleLabel}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1.5,
                  backgroundColor: "rgba(34, 35, 37, 0.02)",
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                    gap: 2,
                  }}
                >
                  {overviewBlocks.map((block, index) => (
                    <Box
                      key={`overview-card-${block.key}`}
                      sx={{
                        pl: { md: index === 0 ? 0 : 2 },
                        borderLeft: { md: index === 0 ? "none" : "1px solid rgba(0,0,0,0.08)" },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.875rem'}}>
                        {block.label}
                      </Typography>
                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                        {block.items.map((item) => {
                          const value = getOverviewSnapshotValue(item.statement, item.code);
                          return (
                            <Box
                              key={`${block.key}-${item.code}`}
                              sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {item.label}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{
                                  textAlign: "right",
                                  minWidth: 72,
                                  color: getMetricValueColor(value, item.code),
                                }}
                              >
                                {formatFinancialValue(value, item.unit_type, scale, item.code)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Stack>
          </Box>
        )}
        
          <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Financials data</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                <strong>Data Source</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Financial statement data displayed on this platform is sourced from the third-party
                market data provider{" "}
                <NavLink to="https://twelvedata.com/" target="_blank" rel="noopener noreferrer">
                  TwelveData
                </NavLink>{" "}
                under a commercial license. Data coverage includes the majority of publicly listed
                U.S. and Canadian companies, with partial coverage of Australian and other
                international issuers.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Data is displayed in the reporting currency provided by the source.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                TTM values are calculated from the last 4 quarters of data, where available.
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                <strong>Standardization and Presentation</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Financial statement line items are standardized to enable comparability across
                issuers and reporting frameworks. Companies may report under U.S. GAAP, IFRS, or
                other applicable standards, and may present line items using different naming
                conventions or classifications.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                To support cross-company analysis:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • Line items may be normalized or mapped to consistent categories.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • Certain subcomponents may be aggregated.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • Statement totals (e.g., operating, investing, and financing cash flows) reflect
                standardized representations of reported values.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                As a result, line-item presentation may differ slightly from a company’s original
                filing format.
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                <strong>Reported vs. Calculated Metrics</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Statement data reflects reported values as provided by the data source.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Certain financial metrics (including, but not limited to, free cash flow, margin
                calculations, leverage ratios, or derived performance indicators) are calculated
                based on available statement data. These metrics are dependent on the completeness
                and structure of underlying data and may not align exactly with company-defined
                non-GAAP measures.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Calculated metrics should be considered analytical tools rather than official
                reported figures.
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                <strong>Reconciliation Considerations</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Depending on source formatting and normalization, certain reconciliation components
                may not appear as standalone line items within this interface.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Users seeking formal reconciliation to issuer filings should refer directly to the
                company’s quarterly and annual reports filed with applicable regulatory authorities.
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                <strong>Data Limitations</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                All financial data is provided “as is” and may be:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • Delayed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • Incomplete
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • Subject to revision or restatement
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • Inaccurate due to source limitations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                While reasonable efforts are made to maintain data integrity, no representation or
                warranty is made regarding completeness or accuracy.
              </Typography>
            </DialogContent>
          </Dialog>

        {!loading && !error && !hasFinancials && (
          <Typography variant="body2" color="text.secondary">
            There is no published financial information for this company available on{" "}
            <NavLink
              to="https://twelvedata.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              TwelveData
            </NavLink>
            , our current data provider.
          </Typography>
        )}

        <Box sx={{ mt: 1 }}>
          <Stack spacing={2}>
            {loading && (
              <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loading && error && (
              <Typography variant="body2" sx={{ color: "error.main" }}>
                {error}
              </Typography>
            )}

            {!loading && !error && hasFinancials && (
              <>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Financials Detail
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Tabs
                      value={statementIndex}
                      onChange={(_, value) => setStatementIndex(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        minHeight: 38,
                        "& .MuiTab-root": {
                          textTransform: "none",
                          fontWeight: 600,
                          minHeight: 38,
                        },
                      }}
                    >
                      {STATEMENT_TABS.map((item) => (
                        <Tab key={item.key} label={item.label} />
                      ))}
                    </Tabs>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {!isOverview && (
                        <FormControl size="small">
                        <Select
                          value={unitScale}
                          onChange={(event) => setUnitScale(event.target.value)}
                          sx={{ fontSize: "0.8125rem" }}
                        >
                          {UNIT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      )}

                      {showExpandCollapse && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={allGroupsExpanded ? collapseAllGroups : expandAllGroups}
                          sx={{ fontWeight: 600 }}
                        >
                          {allGroupsExpanded ? "Collapse all" : "Expand all"}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Stack>

                <Divider />

                {isOverview && overviewPeriods.length > 0 && (
                  <TableShell
                    sx={{
                      overflowX: { xs: "auto", md: "hidden" },
                      WebkitOverflowScrolling: "touch",
                      "& .fin-row:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                    }}
                  >
                    <TableHeaderRow gridTemplateColumns={columnTemplate}>
                      <TableCell label="Period Ending Date" />
                      {overviewPeriods.map((period) => (
                        <TableCell key={period.period_id} align="right" sx={{ pr: 1}}>
                          <Typography variant="caption" fontWeight={700}>
                            {formatDateLabel(period.end_date)}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableHeaderRow>
                    <Stack spacing={0} sx={{ px: 1, py: 0.5 }}>
                      {metricsDetailBlocks.map((block, blockIndex) => (
                        <Box key={block.key}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: columnTemplate,
                              gap: 1,
                              alignItems: "center",
                              pt: blockIndex === 0 ? 0: 2,
                              mt: 2,
                              mb: 1,
                              borderTop: blockIndex === 0 ? "none" : "1px solid rgba(0,0,0,0.15)",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.8125rem'}}>
                              {block.label}
                            </Typography>
                          </Box>
                          {block.items.map((row) => (
                            <Box
                              key={`${block.key}-${row.code}`}
                              className="fin-row"
                              sx={{
                                display: "grid",
                                gridTemplateColumns: columnTemplate,
                                gap: 1,
                                alignItems: "center",
                                py: ROW_PY,
                              }}
                            >
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pl: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>
                                  {row.label}
                                </Typography>
                                {METRIC_DEFINITIONS[row.code] && (
                                  <Tooltip
                                    title={
                                      <Box sx={{ maxWidth: 320 }}>
                                        <Typography variant="caption" fontWeight={700} display="block">
                                          {METRIC_DEFINITIONS[row.code].label}
                                        </Typography>
                                        <Divider sx={{mt:1}}/>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                          <strong>Definition:</strong> {METRIC_DEFINITIONS[row.code].definition}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                          <strong>Interpretation:</strong> {METRIC_DEFINITIONS[row.code].interpretation}
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          backgroundColor: "#ffffff",
                                          color: "text.primary",
                                          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                                          border: "1px solid rgba(0,0,0,0.08)",
                                          p:1.5,
                                        },
                                      },
                                      arrow: {
                                        sx: {
                                          color: "#ffffff",
                                          "&:before": {
                                            border: "1px solid rgba(0,0,0,0.08)",
                                            backgroundColor: "#ffffff",
                                            boxSizing: "border-box",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                      <InfoOutlinedIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                              {overviewPeriods.map((period, index) => {
                                const values = rowValuesForDisplay(row, row.statement);
                                const value = values[index];
                                return (
                                  <Typography
                                    key={`${row.code}-${period.period_id}`}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      textAlign: isRightAlignedValue(row.unit_type) ? "right" : "left",
                                      pr: 1,
                                    }}
                                  >
                                    {formatFinancialValue(value, row.unit_type, scale, row.code)}
                                  </Typography>
                                );
                              })}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Stack>
                  </TableShell>
                )}

                {!isOverview && rows.length > 0 && (
                  <TableShell
                    sx={{
                      overflowX: { xs: "auto", md: "hidden" },
                      WebkitOverflowScrolling: "touch",
                      "& .fin-row:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                    }}
                  >
                    <TableHeaderRow gridTemplateColumns={columnTemplate}>
                      <TableCell label="Period Ending Date" />
                      {periods.map((period) => (
                        <TableCell key={period.period_id} align="right" sx={{ pr: 1 }}>
                          <Typography variant="caption" fontWeight={700}>
                            {formatDateLabel(period.end_date)}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableHeaderRow>
                    <Stack spacing={0} sx={{ px: 1, py: 0.5 }}>
                      {layout.map((entry, entryIndex) => {
                        if (entry.type === "section_header") {
                          return (
                            <Box
                              key={`${entry.label}-${entryIndex}`}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: columnTemplate,
                                gap: 1,
                                alignItems: "center",
                                pt: entryIndex === 0 ? 0: 2,
                                mt: 2,
                                mb: 1,
                                borderTop: entryIndex === 0 ? "none" : "1px solid rgba(0,0,0,0.15)",
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{fontSize:'0.8125rem'}}>
                                {entry.label}
                              </Typography>
                            </Box>
                          );
                        }
                        if (entry.type === "row") {
                          const row = entry.row;
                          return (
                            <Box
                              key={`${row.code}-${entryIndex}`}
                              className="fin-row"
                              sx={{
                                display: "grid",
                                gridTemplateColumns: columnTemplate,
                                gap: 1,
                                alignItems: "center",
                                pt: SUBTOTAL_CODES.has(row.code)
                                  ? 2
                                  : ROW_PY,
                                mt: row.code === "sales" ? 1 : 0,
                                pb: ROW_PY,
                                borderTop: SUBTOTAL_CODES.has(row.code)
                                  ? "1px solid rgba(0,0,0,0.08)"
                                  : "none",
                              }}
                            >
                              <Typography variant="caption" fontWeight={600} sx={{ pl: 1.5 }}>
                                {row.label}
                              </Typography>
                              {periods.map((period, index) => {
                                const values = rowValuesForDisplay(row);
                                const value = values[index];
                                return (
                                  <Typography
                                    key={`${row.code}-${period.period_id}`}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      textAlign: isRightAlignedValue(row.unit_type) ? "right" : "left",
                                      pr: 1,
                                    }}
                                  >
                                    {formatFinancialValue(value, row.unit_type, scale, row.code)}
                                  </Typography>
                                );
                              })}
                            </Box>
                          );
                        }

                        const expanded = isGroupExpanded(entry.key);
                        const totalRow = entry.totalRow;
                        return (
                          <Box key={entry.key}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: columnTemplate,
                                gap: 1,
                                alignItems: "center",
                                py: ROW_PY,
                              }}
                            >
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <IconButton
                                  size="small"
                                  onClick={() => toggleGroup(entry.key)}
                                  aria-label={`Toggle ${entry.label}`}
                                  sx={{ width: 24, height: 24 }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: "0.75rem",
                                      lineHeight: 1,
                                      ml:1.5,
                                      color: "text.secondary",
                                    }}
                                  >
                                    {expanded ? "▼" : "▶"}
                                  </Box>
                                </IconButton>
                                <Typography variant="caption" fontWeight={600}>
                                  {entry.label}
                                </Typography>
                              </Stack>
                              {periods.map((period, index) => {
                                const values = totalRow ? rowValuesForDisplay(totalRow) : [];
                                const value = values[index];
                                return (
                                  <Typography
                                    key={`${entry.key}-${period.period_id}`}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      textAlign: totalRow && isRightAlignedValue(totalRow.unit_type) ? "right" : "left",
                                      pr: 1,
                                    }}
                                  >
                                    {totalRow
                                      ? formatFinancialValue(value, totalRow.unit_type, scale, totalRow.code)
                                      : "—"}
                                  </Typography>
                                );
                              })}
                            </Box>
                            {expanded &&
                              entry.rows.map((row) => {
                                if (row.type === "subgroup") {
                                  const subgroupKey = `${entry.key}::${row.key}`;
                                  const subgroupExpanded = isGroupExpanded(subgroupKey);
                                  const subgroupTotalRow = row.totalRow;
                                  return (
                                    <Box key={subgroupKey}>
                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: columnTemplate,
                                          gap: 1,
                                          alignItems: "center",
                                          py: ROW_PY,
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          spacing={0.75}
                                          alignItems="center"
                                          sx={{ pl: 2 }}
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() => toggleGroup(subgroupKey)}
                                            aria-label={`Toggle ${row.label}`}
                                            sx={{ width: 24, height: 24 }}
                                          >
                                            <Box
                                              component="span"
                                              sx={{
                                                fontSize: "0.75rem",
                                                lineHeight: 1,
                                                color: "text.secondary",
                                              }}
                                            >
                                              {subgroupExpanded ? "▼" : "▶"}
                                            </Box>
                                          </IconButton>
                                          <Typography variant="caption" fontWeight={600}>
                                            {row.label}
                                          </Typography>
                                        </Stack>
                                        {periods.map((period, index) => {
                                          const values = subgroupTotalRow
                                            ? rowValuesForDisplay(subgroupTotalRow)
                                            : [];
                                          const value = values[index];
                                          return (
                                            <Typography
                                              key={`${subgroupKey}-${period.period_id}`}
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{
                                                textAlign:
                                                  subgroupTotalRow && isRightAlignedValue(subgroupTotalRow.unit_type)
                                                    ? "right"
                                                    : "left",
                                                pr: 1,
                                              }}
                                            >
                                              {subgroupTotalRow
                                                ? formatFinancialValue(
                                                    value,
                                                    subgroupTotalRow.unit_type,
                                                    scale,
                                                    subgroupTotalRow.code
                                                  )
                                                : "—"}
                                            </Typography>
                                          );
                                        })}
                                      </Box>
                                      {subgroupExpanded &&
                                        row.rows.map((childRow) => (
                                          <Box
                                            key={childRow.code}
                                            className="fin-row"
                                            sx={{
                                              display: "grid",
                                              gridTemplateColumns: columnTemplate,
                                              gap: 1,
                                              alignItems: "center",
                                              py: ROW_PY,
                                            }}
                                          >
                                            <Typography variant="caption" fontWeight={600} sx={{ pl: 6 }}>
                                              {childRow.label}
                                            </Typography>
                                            {periods.map((period, index) => {
                                              const values = rowValuesForDisplay(childRow);
                                              const value = values[index];
                                              return (
                                                <Typography
                                                  key={`${childRow.code}-${period.period_id}`}
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{
                                                    textAlign: isRightAlignedValue(childRow.unit_type) ? "right" : "left",
                                                    pr: 1,
                                                  }}
                                                >
                                                  {formatFinancialValue(value, childRow.unit_type, scale, childRow.code)}
                                                </Typography>
                                              );
                                            })}
                                          </Box>
                                        ))}
                                    </Box>
                                  );
                                }

                                return (
                                  <Box
                                    key={row.code}
                                    className="fin-row"
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: columnTemplate,
                                      gap: 1,
                                      alignItems: "center",
                                      py: ROW_PY,
                                    }}
                                  >
                                    <Typography variant="caption" fontWeight={600} sx={{ pl: 4 }}>
                                      {row.label}
                                    </Typography>
                                    {periods.map((period, index) => {
                                      const values = rowValuesForDisplay(row);
                                      const value = values[index];
                                      return (
                                        <Typography
                                          key={`${row.code}-${period.period_id}`}
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            textAlign: isRightAlignedValue(row.unit_type) ? "right" : "left",
                                            pr: 1,
                                          }}
                                        >
                                          {formatFinancialValue(value, row.unit_type, scale, row.code)}
                                        </Typography>
                                      );
                                    })}
                                  </Box>
                                );
                              })}
                          </Box>
                        );
                      })}
                    </Stack>
                  </TableShell>
                )}
              </>
            )}

          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
